import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import { PatientContext, FamilyContact } from '~/types/coordinate';

interface Props {
  patientContext: PatientContext | undefined;
  onUpdate: (context: Partial<PatientContext>) => void;
}

const AddContactDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (contact: FamilyContact) => void;
}> = ({ open, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [phone, setPhone] = useState('');
  const [isDecisionMaker, setIsDecisionMaker] = useState(false);
  const [notes, setNotes] = useState('');

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      id: `fc-${Date.now()}`,
      name: name.trim(),
      relationship: relationship.trim(),
      phone: phone.trim(),
      isDecisionMaker,
      notes: notes.trim() || undefined,
    });
    setName('');
    setRelationship('');
    setPhone('');
    setIsDecisionMaker(false);
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Family Contact</DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Stack direction="row" spacing={2}>
          <TextField size="small" fullWidth label="Name" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <TextField size="small" fullWidth label="Relationship" value={relationship} onChange={e => setRelationship(e.target.value)} />
        </Stack>
        <TextField size="small" fullWidth label="Phone" value={phone} onChange={e => setPhone(e.target.value)} />
        <FormControlLabel
          control={<Checkbox checked={isDecisionMaker} onChange={e => setIsDecisionMaker(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Primary decision-maker</Typography>}
        />
        <TextField size="small" fullWidth multiline rows={2} label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!name.trim()}>Add</Button>
      </DialogActions>
    </Dialog>
  );
};

const ConsentSection: React.FC<{ context: PatientContext; onUpdate: (c: Partial<PatientContext>) => void }> = ({ context, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(context.consentNotes ?? '');

  const handleDocument = () => {
    onUpdate({
      consentDocumented: true,
      consentNotes: notes.trim() || undefined,
      consentTimestamp: new Date().toISOString(),
    });
    setEditing(false);
  };

  if (context.consentDocumented) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18, mt: 0.2 }} />
        <Box>
          <Typography variant="body2" fontWeight={600} color="success.dark">Consent documented</Typography>
          {context.consentNotes && (
            <Typography variant="caption" color="text.secondary">{context.consentNotes}</Typography>
          )}
          {context.consentTimestamp && (
            <Typography variant="caption" color="text.disabled" display="block">
              {new Date(context.consentTimestamp).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <RadioButtonUncheckedIcon sx={{ color: 'warning.main', fontSize: 18 }} />
        <Typography variant="body2" color="warning.dark" fontWeight={600}>Consent not yet documented</Typography>
      </Stack>
      {editing ? (
        <Box>
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Consent notes"
            placeholder="Describe what was discussed and agreed to…"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={handleDocument}
              sx={{ bgcolor: '#00bda1', '&:hover': { bgcolor: '#009e87' } }}>
              Document Consent
            </Button>
            <Button size="small" onClick={() => setEditing(false)}>Cancel</Button>
          </Stack>
        </Box>
      ) : (
        <Button size="small" variant="outlined" onClick={() => setEditing(true)}
          sx={{ color: '#153873', borderColor: 'rgba(21,56,115,0.4)', '&:hover': { borderColor: '#153873', bgcolor: 'rgba(21,56,115,0.04)' } }}>
          Document Consent
        </Button>
      )}
    </Box>
  );
};

const PatientFamilyPanel: React.FC<Props> = ({ patientContext, onUpdate }) => {
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [editingNotes, setEditingNotes] = useState<null | 'preference' | 'home' | 'transport'>(null);
  const [notesDraft, setNotesDraft] = useState('');

  const ctx: PatientContext = patientContext ?? { consentDocumented: false, familyContacts: [] };

  const startEdit = (field: 'preference' | 'home' | 'transport') => {
    const current = field === 'preference'
      ? ctx.preferredProviderNotes
      : field === 'home'
        ? ctx.homeEnvironmentNotes
        : ctx.transportationNotes;
    setNotesDraft(current ?? '');
    setEditingNotes(field);
  };

  const saveNotes = () => {
    if (editingNotes === 'preference') onUpdate({ preferredProviderNotes: notesDraft.trim() || undefined });
    if (editingNotes === 'home') onUpdate({ homeEnvironmentNotes: notesDraft.trim() || undefined });
    if (editingNotes === 'transport') onUpdate({ transportationNotes: notesDraft.trim() || undefined });
    setEditingNotes(null);
  };

  const handleAddContact = (contact: FamilyContact) => {
    onUpdate({ familyContacts: [...ctx.familyContacts, contact] });
  };

  const notesFields: Array<{ key: 'preference' | 'home' | 'transport'; label: string; placeholder: string }> = [
    { key: 'preference', label: 'Patient preferences', placeholder: 'Provider preferences, timing, other stated preferences…' },
    { key: 'home', label: 'Home environment', placeholder: 'Living situation, access, stairs, caregiver availability…' },
    { key: 'transport', label: 'Transportation', placeholder: 'Transportation needs, constraints, insurance coverage…' },
  ];

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>Patient & Family</Typography>

      {/* Consent */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem', mb: 0.75 }}>
          Consent
        </Typography>
        <ConsentSection context={ctx} onUpdate={onUpdate} />
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      {/* Notes fields */}
      {notesFields.map(({ key, label, placeholder }) => {
        const value = key === 'preference' ? ctx.preferredProviderNotes : key === 'home' ? ctx.homeEnvironmentNotes : ctx.transportationNotes;
        const isEditing = editingNotes === key;
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem' }}>
                {label}
              </Typography>
              {!isEditing && (
                <Button size="small" sx={{ fontSize: '0.7rem', minWidth: 0, px: 0.75, py: 0.25 }} onClick={() => startEdit(key)}>
                  {value ? 'Edit' : 'Add'}
                </Button>
              )}
            </Stack>
            {isEditing ? (
              <Box>
                <TextField
                  fullWidth size="small" multiline rows={3}
                  placeholder={placeholder}
                  value={notesDraft}
                  onChange={e => setNotesDraft(e.target.value)}
                  autoFocus
                  sx={{ mb: 1 }}
                />
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="contained" onClick={saveNotes}>Save</Button>
                  <Button size="small" onClick={() => setEditingNotes(null)}>Cancel</Button>
                </Stack>
              </Box>
            ) : value ? (
              <Typography variant="body2" sx={{ fontSize: '0.82rem', whiteSpace: 'pre-wrap' }}>{value}</Typography>
            ) : (
              <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>Not documented</Typography>
            )}
          </Box>
        );
      })}

      <Divider sx={{ mb: 2.5 }} />

      {/* Family contacts */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, letterSpacing: 0.5, fontSize: '0.65rem' }}>
            Family & Contacts
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />}
            onClick={() => setAddContactOpen(true)}
            sx={{ fontSize: '0.75rem' }}
          >
            Add Contact
          </Button>
        </Stack>

        {ctx.familyContacts.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.8rem' }}>No contacts added</Typography>
        ) : (
          ctx.familyContacts.map(contact => (
            <Box
              key={contact.id}
              sx={{
                p: 1.25,
                mb: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: contact.isDecisionMaker ? 'primary.200' : 'divider',
                bgcolor: contact.isDecisionMaker ? 'primary.50' : 'transparent',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem' }}>{contact.name}</Typography>
                <Typography variant="caption" color="text.secondary">{contact.relationship}</Typography>
                {contact.isDecisionMaker && (
                  <Chip
                    label="Decision-maker"
                    size="small"
                    icon={<StarIcon sx={{ fontSize: 11 }} />}
                    sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: 'primary.100', color: 'primary.dark' }}
                  />
                )}
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                <PhoneIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>{contact.phone}</Typography>
              </Stack>
              {contact.notes && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.72rem' }}>
                  {contact.notes}
                </Typography>
              )}
            </Box>
          ))
        )}
      </Box>

      <AddContactDialog
        open={addContactOpen}
        onClose={() => setAddContactOpen(false)}
        onSave={handleAddContact}
      />
    </Box>
  );
};

export default PatientFamilyPanel;
